import React, { useEffect, useRef } from "react";
import { Loader2Icon, CameraIcon, CameraOffIcon } from "lucide-react";
import { MdMic, MdMicOff } from "react-icons/md";

import "./RemoteVideo.scss";

const RemoteVideo = ({
  stream,
  userId,
  isVideoOn,
  isAudioOn,
  userName,
  className = "",
  isPip = false,
  pipWindow = null,
  userData,
  isDisconnected = false,
  onRemoveDisconnected,
}) => {
  const containerRef = useRef(null);
  const disconnectTimerRef = useRef(null);
  const [timeRemaining, setTimeRemaining] = React.useState(120);

  useEffect(() => {
    if (isDisconnected) {
      disconnectTimerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(disconnectTimerRef.current);
            disconnectTimerRef.current = null;
            if (onRemoveDisconnected) {
              onRemoveDisconnected(userId);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (disconnectTimerRef.current) {
        clearInterval(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setTimeRemaining(120);
    }
    return () => {
      if (disconnectTimerRef.current) {
        clearInterval(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [isDisconnected, userId, onRemoveDisconnected]);

  useEffect(() => {
    if (!stream || !userId || !containerRef.current || isDisconnected) return;

    let mounted = true;

    const attachVideo = async () => {
      if (!mounted) return;
      try {
        const videoPlayerContainer = containerRef.current?.querySelector(
          "video-player-container"
        );

        if (!videoPlayerContainer) {
          console.warn(`video-player-container not found in containerRef`);
          return;
        }

        if (isVideoOn) {
          videoPlayerContainer.innerHTML = "";
          await new Promise((resolve) => setTimeout(resolve, 0));

          const videoElement = await stream.attachVideo(userId, 3);
          videoElement.style.width = "100%";
          videoElement.style.height = "100%";
          videoElement.style.position = "absolute";
          videoElement.style.top = "0";
          videoElement.style.left = "0";

          videoPlayerContainer.appendChild(videoElement);

          console.log("Video element created:", videoElement);
          console.log("Video element type:", videoElement.tagName);

          // Function to find actual video element and check aspect ratio
          const checkAndApplyAspectRatio = () => {
            // Zoom SDK uses custom VIDEO-PLAYER element, find actual <video> inside
            let actualVideo = videoElement;

            // If it's a custom element, look for <video> inside
            if (videoElement.tagName === "VIDEO-PLAYER") {
              actualVideo = videoElement.querySelector("video");
              if (!actualVideo) {
                // Try shadow DOM
                if (videoElement.shadowRoot) {
                  actualVideo = videoElement.shadowRoot.querySelector("video");
                }
              }
            }

            console.log("Actual video element found:", actualVideo?.tagName);

            if (actualVideo && actualVideo.tagName === "VIDEO") {
              const width = actualVideo.videoWidth;
              const height = actualVideo.videoHeight;

              console.log(
                `Checking dimensions for user ${userId}: ${width}x${height}`
              );

              if (width && height) {
                const aspectRatio = width / height;
                console.log(
                  `User ${userId} (${userName}) video dimensions: ${width}x${height}, aspect ratio: ${aspectRatio.toFixed(2)}`
                );

                // Portrait video (mobile) typically has aspect ratio < 1
                // Landscape video (desktop) typically has aspect ratio > 1
                const isMobileVideo = aspectRatio < 1;

                console.log(
                  `User ${userId} detected as: ${isMobileVideo ? "Mobile (Portrait)" : "Desktop (Landscape)"}`
                );
                const isMobile = /iPhone|iPad|iPod|Android/i.test(
                  navigator.userAgent
                );

                // Apply appropriate object-fit based on detection
                actualVideo.style.objectFit =
                  isMobileVideo || isMobile ? "contain" : "cover";
                videoElement.className = "zoom-video-element";

                return true;
              }
            }
            return false;
          };

          if (!checkAndApplyAspectRatio()) {
            // Fallback: poll for dimensions
            let attempts = 0;
            const pollInterval = setInterval(() => {
              attempts++;
              console.log(`Polling attempt ${attempts} for user ${userId}`);
              if (checkAndApplyAspectRatio() || attempts > 30) {
                clearInterval(pollInterval);
                if (attempts > 30) {
                  console.warn(
                    `Failed to detect video dimensions for user ${userId} after ${attempts} attempts`
                  );
                }
              }
            }, 200);
          }
        } else {
          videoPlayerContainer.innerHTML = "";
          try {
            await stream.detachVideo(userId);
          } catch {}
        }
      } catch (error) {
        console.error(`Error handling video for user ${userId}:`, error);
      }
    };

    attachVideo();

    return () => {
      mounted = false;
      if (stream && userId) {
        try {
          stream.detachVideo(userId).catch((e) => {
            console.log("Cleanup detach promise error:", e);
          });
        } catch (e) {
          console.log("Cleanup detach error:", e);
        }
      }
    };
  }, [
    stream,
    userId,
    isVideoOn,
    isAudioOn,
    isDisconnected,
    userName,
    isPip,
    pipWindow,
  ]);

  useEffect(() => {
    if (isDisconnected && containerRef.current) {
      const videoPlayerContainer = containerRef.current.querySelector(
        "video-player-container"
      );
      if (videoPlayerContainer) videoPlayerContainer.innerHTML = "";
      if (stream && userId) {
        try {
          stream.detachVideo(userId).catch((e) => {
            console.log("Detach promise error on disconnect:", e);
          });
        } catch (e) {
          console.log("Detach error on disconnect:", e);
        }
      }
    }
  }, [isDisconnected, stream, userId]);

  if (isDisconnected) {
    return (
      <div
        className={`remote-video disconnected ${className} ${isPip ? "pip-mode" : ""}`}
      >
        <div
          ref={containerRef}
          className="video-container disconnected-bg"
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            backgroundColor: "#2d2d2d",
          }}
        >
          <video-player-container
            id={`video-player-container-${userId}`}
            style={{ width: "100%", height: "100%", position: "relative" }}
          ></video-player-container>
        </div>

        <div className="disconnected-overlay">
          <div className="disconnected-content">
            <div className="spinner-container">
              <Loader2Icon className="reconnection-spinner" />
            </div>
            <p className="disconnected-text">Connection Lost</p>
            <p className="reconnecting-text">
              Reconnecting... {timeRemaining}s
            </p>
          </div>
        </div>

        <div className="bottom-overlay disconnected-overlay-info">
          <div className="overlay-content">
            <div className="user-details">
              <div className="video-status">
                <div className="status-dot disconnected"></div>
              </div>
              <span className="user-name">{userName || "User"}</span>
              <span className="disconnect-timer">{timeRemaining}s</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`remote-video ${className} ${isPip ? "pip-mode " : ""}`}>
      <div
        ref={containerRef}
        className="video-container"
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          backgroundColor: "#1a1a1a",
          overflow: "hidden",
        }}
      >
        {/* Static Zoom SDK container element */}
        <video-player-container
          id={`video-player-container-${userId}`}
          style={{ width: "100%", height: "100%", position: "relative" }}
        ></video-player-container>
      </div>

      {!isVideoOn && (
        <div className="video-off-placeholder">
          <div className="user-avatar">
            <div className="avatar-circle">
              {(userName || "User").charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      )}

      <div className={`bottom-overlay ${isPip ? "pip-bottom-overlay " : ""}`}>
        <div className="overlay-content">
          <div className="user-details">
            <span className="user-name">{userName || "User"}</span>
            <div className="video-status">
              {isVideoOn ? (
                <CameraIcon className="camera" />
              ) : (
                <CameraOffIcon className="camera" />
              )}
              {isAudioOn ? (
                <MdMic className="camera" />
              ) : (
                <MdMicOff className="camera" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteVideo;
